import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { checkAuthSession, createAuthSession } from "@/api/auth";
import { useAuth } from "@/store/authStore";
import Offer from "@/components/Offer/Offer";
import AuthMapBackground from "./AuthMapBackground";
import PhoneLoginForm from "./PhoneLoginForm";
import cls from "./styles.module.scss";

interface PendingSession {
  sessionId: string;
}

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      fill="currentColor"
      d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71l-4.14-3.05-1.99 1.93c-.23.23-.42.42-.83.41z"
    />
  </svg>
);

function hasValidInitData(initData: string | null): boolean {
  if (!initData) return false;

  if (!initData.includes("=")) {
    return true;
  }

  return Boolean(new URLSearchParams(initData).get("hash"));
}

function isIosBrowser(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

export default function AuthRequired({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const { initData, sessionToken, setAuth, setSessionToken } = useAuth();
  const [pendingSession, setPendingSession] = useState<PendingSession | null>(
    null
  );
  const [loading, setLoading] = useState(false);

  const isSravniDohod = __SERVICE_NAME__ === "sravni_dohod";
  const isAuthorized = Boolean(hasValidInitData(initData) || sessionToken);

  useEffect(() => {
    if (!isSravniDohod || isAuthorized || !pendingSession) return;

    const id = window.setInterval(async () => {
      try {
        const session = await checkAuthSession(pendingSession.sessionId);

        if (session.status === "expired") {
          setPendingSession(null);
          return;
        }

        if (session.approved && session.token && session.telegram_id) {
          setSessionToken(session.token);
          setAuth(String(session.telegram_id), "telegram_session");
        }
      } catch (e) {
        console.error("Failed to check auth session:", e);
      }
    }, 2000);

    return () => window.clearInterval(id);
  }, [isSravniDohod, isAuthorized, pendingSession, setAuth, setSessionToken]);

  const openBot = async () => {
    if (loading) return;

    setLoading(true);
    const iosTelegramWindow = isIosBrowser()
      ? window.open("about:blank", "_blank")
      : null;

    try {
      const session = await createAuthSession();
      const nextPendingSession = { sessionId: session.session_id };
      setPendingSession(nextPendingSession);

      const botName = __BOT_NAME__.replace(/^@/, "");
      const botUrl = `https://t.me/${botName}?start=login_${encodeURIComponent(
        nextPendingSession.sessionId
      )}`;

      if (iosTelegramWindow) {
        iosTelegramWindow.location.href = botUrl;
      } else {
        window.open(botUrl, "_blank");
      }
    } catch (e) {
      console.error("Failed to create auth session:", e);
      setPendingSession(null);
      iosTelegramWindow?.close();
    } finally {
      setLoading(false);
    }
  };

  if (!isSravniDohod || isAuthorized) return <>{children}</>;

  return (
    <div className={cls.app}>
      <section className={cls.login}>
        <div className={cls.mapBackdrop} aria-hidden="true">
          <AuthMapBackground />
        </div>
        <div className={cls.mapVeil} />

        <div className={cls.loginInner}>
          <div className={cls.card}>
            <div className={cls.brandIcon}>
              <img src="/assets/icons/icon-192.png" alt="" />
            </div>
            <h1>{t("authRequired.title")}</h1>
            <p className={cls.sub}>{t("authRequired.subtitle")}</p>

            <PhoneLoginForm
              onAuthorized={(session) => {
                setSessionToken(session.token);
                setAuth(String(session.telegram_id), "phone");
              }}
            />

            <div className={cls.divider}>{t("authRequired.or")}</div>

            <button
              className={`${cls.btn} ${cls.btnTg}`}
              type="button"
              onClick={openBot}
              disabled={loading}
            >
              {loading ? <span className={cls.spinner} /> : <TelegramIcon />}
              {t("authRequired.telegramButton")}
            </button>

            <div className={cls.offer}>
              <Offer label={t("authRequired.terms")} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
